import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import React, { useEffect, useState } from 'react';
import { useDispatch, useIntl, useLocation, useSelector } from 'umi';
import { Button, Flex, Form, Space } from 'antd';
import styles from './index.less';
import { searchCompanyCustomersRequest } from '@/services/api/customer';
import { ContactModel, CUSTOMER_TYPES, CustomerType } from '@/typings/models/Contact';
import { findExistingContact } from '@/components/LeadSource/ManualLead/findExistingContact';
import { findMatchingCompany } from '@/utils/contacts';
import { hasContactIdentity } from '@/components/Contacts/ContactFields';
import ProjectContactSelect from '@/components/LeadSource/ManualLead/ProjectContactSelect';
import { LeadModel } from '@/typings/models/Lead';
import { processAPIResponseNow } from '@/utils/request';
import { ROUTE_PATHS } from '../../../../config/RoutePaths';
import { LEAD_VIEWS } from '@/components/Lead/constants';
import pick from 'lodash/pick';
import * as LeadSelectors from '@/selectors/leadSelectors';
import * as LeadStateSelectors from '@/selectors/leadStateSelectors';

import AddProjectRelatedLeads from '@/components/LeadSource/ManualLead/AddProjectRelatedLeads';
import CustomerForm from '@/components/LeadSource/ManualLead/CustomerForm';
import ProjectForm from '@/components/LeadSource/ManualLead/ProjectForm';
import {
  showCustomerExistsModal,
  SKIP_CUSTOMER_EXISTS_WARNING_KEY,
} from '@/components/LeadSource/ManualLead/CustomerExistsModal';
import { useCustomerRequiredFields } from '@/utils/hooks';
import { useFixedBranchService } from '@/components/LeadSource/ManualLead/ownBranchService';

// Customer fields that should be nested under 'customer' in the payload
const CUSTOMER_FIELDS = ['customerId', 'firstname', 'lastname', 'email', 'phone', 'locale', 'gender'];

const AddManualLead: React.FC = () => {
  const dispatch = useDispatch();
  const customerRequiredFields = useCustomerRequiredFields();
  const intl = useIntl();
  const location = useLocation();
  const [customerForm] = Form.useForm();
  const [projectForm] = Form.useForm();

  const submitLoading = useSelector(
    (state: DefaultRootState) => state.loading.effects['leads/create'],
  );

  const leadStates = useSelector(LeadStateSelectors.selectStates);
  const addLeadDrawer = useSelector(LeadSelectors.selectAddLeadDrawer);
  const stateId = addLeadDrawer?.stateId || leadStates[0]?.id;


  // State for extra field values
  const [customerExtraFieldValues, setCustomerExtraFieldValues] = useState<any>({});
  const [projectExtraFieldValues, setProjectExtraFieldValues] = useState<any>({});

  // State for related leads
  const [foundCustomers, setFoundCustomers] = useState<any[]>([]);
  const [isCustomerFormDisabled, setIsCustomerFormDisabled] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);

  const [customerType, setCustomerType] = useState<CustomerType>(CUSTOMER_TYPES.PERSON);
  const [matchingCompany, setMatchingCompany] = useState<any>(null);
  // For an existing customer: which of its contacts leads the new project.
  const [projectContactId, setProjectContactId] = useState<string | undefined>();
  const existingCustomerId: string | undefined = Form.useWatch('customerId', customerForm);


  // Pre-fill form from payload if available
  useEffect(() => {
    if (addLeadDrawer) {
      const { email, phone, firstname, lastname } = addLeadDrawer;
      if (email || phone || firstname || lastname) {
        customerForm.setFieldsValue({
          email: email || '',
          phone: phone || '',
          firstname: firstname || '',
          lastname: lastname || '',
        });

        // If email or phone is provided, trigger search
        if (email || phone) {
          searchContacts(email, phone);
        }
      }
    }
  }, [addLeadDrawer, customerForm]);

  // An agent carried by the payload (e.g. the calendar slot's resource) wins over
  // the field's own "assign to me" default.
  const payloadAssignee = (addLeadDrawer as { assignee?: string })?.assignee;
  useEffect(() => {
    if (!payloadAssignee) return;
    projectForm.setFieldsValue({ assignee_id: payloadAssignee });
  }, [payloadAssignee, projectForm]);

  const { branchServiceId: payloadBranchServiceId, branchId: payloadBranchId } =
    (addLeadDrawer as { branchServiceId?: string; branchId?: string }) || {};
  useEffect(() => {
    if (!payloadBranchServiceId) return;
    projectForm.setFieldsValue({ branch_service_id: payloadBranchServiceId });
  }, [payloadBranchServiceId, projectForm]);

  // Without a service from the payload, a user with a single service gets it fixed.
  const { service: fixedBranchService, loading: fixedBranchServiceLoading } =
    useFixedBranchService(!payloadBranchServiceId);
  useEffect(() => {
    if (!fixedBranchService) return;
    projectForm.setFieldsValue({ branch_service_id: fixedBranchService.id });
  }, [fixedBranchService, projectForm]);

  useEffect(() => {
    if (stateId) {
      projectForm.setFieldsValue({
        lead_state_id: stateId,
      });
    }
  }, [stateId, projectForm]);


  // An existing contact with this email or phone: the project goes to their customer, with them as its primary contact.
  const searchContacts = async (email?: string, phone?: string) => {
    if (!email && !phone) {
      setFoundCustomers([]);
      setIsCustomerFormDisabled(false);
      setIsSearchingCustomers(false);
      return;
    }

    setIsSearchingCustomers(true);

    try {
      const found = await findExistingContact(email, phone);
      if (!found) {
        setFoundCustomers([]);
        setIsCustomerFormDisabled(false);
        setIsSearchingCustomers(false);
        return;
      }

      setFoundCustomers([found.customer]);
      const useFoundContact = () => applyExistingCustomer(found.customer, found.contact);

      // Check if user has opted to skip the warning
      if (localStorage.getItem(SKIP_CUSTOMER_EXISTS_WARNING_KEY) === 'true') {
        useFoundContact();
        setIsSearchingCustomers(false);
        return;
      }

      showCustomerExistsModal({
        intl,
        customer: found.customer,
        contact: found.contact,
        onConfirm: useFoundContact,
        onCancel: () => {
          // Reset email and phone fields
          customerForm.setFieldsValue({
            email: '',
            phone: '',
          });
          setFoundCustomers([]);
          setIsCustomerFormDisabled(false);
        },
        onAfterClose: () => {
          setIsSearchingCustomers(false);
        },
      });
    } catch (error) {
      console.error('Error searching contacts:', error);
      setFoundCustomers([]);
      setIsCustomerFormDisabled(false);
      setIsSearchingCustomers(false);
    }
  };

  // The project goes to this existing customer, led by `contact` (else its primary); the customer's data stays untouched.
  const applyExistingCustomer = (customer: any, contact?: ContactModel) => {
    const person = contact || customer;
    customerForm.setFieldsValue({
      customerId: customer.id,
      firstname: person.firstname || '',
      lastname: person.lastname || '',
      email: person.email || '',
      phone: person.phone || '',
      locale: person.locale || '',
      gender: person.gender || '',
      company_name: customer.company_name || '',
    });
    setProjectContactId(contact?.id);
    setCustomerType(customer.type === CUSTOMER_TYPES.ORGANIZATION ? CUSTOMER_TYPES.ORGANIZATION : CUSTOMER_TYPES.PERSON);
    setMatchingCompany(null);
    setIsCustomerFormDisabled(true);
  };

  // Companies are never deduplicated by name on the server, so warn here.
  const handleCompanyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const companyName = e.target.value.trim();
    if (!findMatchingCompany([{ company_name: companyName }], companyName)) {
      setMatchingCompany(null);
      return;
    }
    searchCompanyCustomersRequest(companyName).then((response) =>
      processAPIResponseNow(
        response,
        {
          onSuccess: (customers: any[]) => setMatchingCompany(findMatchingCompany(customers, companyName) || null),
          onError: () => setMatchingCompany(null),
        },
        'data',
      ),
    );
  };

  const handleUseMatchingCompany = () => {
    if (!matchingCompany) return;
    setFoundCustomers([matchingCompany]);
    applyExistingCustomer(matchingCompany);
  };

  // A company is matched by its name (handleCompanyBlur); its contact person's
  // email or phone would only offer some other customer that person belongs to.
  const matchesByContact = customerType !== CUSTOMER_TYPES.ORGANIZATION;

  // Handle email blur
  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value;
    if (email && matchesByContact) {
      await searchContacts(email, undefined);
    }
  };

  // Handle phone blur
  const handlePhoneBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    if (phone && matchesByContact) {
      await searchContacts(undefined, phone);
    }
  };

  // Handle using different email/phone
  const handleUseDifferentContact = () => {
    setProjectContactId(undefined);
    // Reset only email and phone fields
    customerForm.setFieldsValue({
      email: '',
      phone: '',
    });

    // Clear related leads state
    setFoundCustomers([]);
    setMatchingCompany(null);

    // Re-enable the customer form
    setIsCustomerFormDisabled(false);
  };

  // Handle lead creation
  const onCreateLead = async () => {
    try {

      // Validate both forms
      await customerForm.validateFields();
      await projectForm.validateFields();

      // Get form values
      const customerFormData = customerForm.getFieldsValue();
      const projectFormData = projectForm.getFieldsValue();

      // Separate customer fields from the rest
      const customer = pick(customerFormData, CUSTOMER_FIELDS);

      // Transform extra fields to array format
      const customerExtraFieldsArray = Object.entries(customerExtraFieldValues)
        .map(([key, value]) => ({ key, value }))
        .filter((field) => field.value !== null && field.value !== undefined && field.value !== '');
      const projectExtraFieldsArray = Object.entries(projectExtraFieldValues)
        .map(([key, value]) => ({ key, value }))
        .filter((field) => field.value !== null && field.value !== undefined && field.value !== '');

      const companyFields =
        customerType === CUSTOMER_TYPES.ORGANIZATION
          ? {
            type: CUSTOMER_TYPES.ORGANIZATION,
            company_name: customerFormData.company_name?.trim(),
            ...(customerFormData.role?.trim() ? { role: customerFormData.role.trim() } : {}),
          }
          : {};
      const extraFieldsPayload = customerExtraFieldsArray.length > 0 ? { extra_fields: customerExtraFieldsArray } : {};
      // A company's contact person goes in `contacts`, not in the customer fields:
      // the backend matches existing customers by `customer.email`/`phone` and
      // would turn a private customer with that email into this company.
      const contactPerson = Object.fromEntries(
        Object.entries(pick(customer, ['firstname', 'lastname', 'email', 'phone', 'gender', 'locale']))
          .map(([field, value]) => [field, typeof value === 'string' ? value.trim() : value])
          .filter(([, value]) => value),
      );
      const isNewCompany = !isCustomerFormDisabled && customerType === CUSTOMER_TYPES.ORGANIZATION;
      const customerPayload = isCustomerFormDisabled
        ? { id: customer.customerId }
        : isNewCompany
          ? { type: CUSTOMER_TYPES.ORGANIZATION, company_name: customerFormData.company_name?.trim(), ...extraFieldsPayload }
          : { ...customer, ...companyFields, ...extraFieldsPayload };
      const contactsPayload = isCustomerFormDisabled
        ? projectContactId && [{ id: projectContactId, is_primary: true }]
        : isNewCompany &&
          hasContactIdentity(contactPerson) && [
            { ...contactPerson, role: customerFormData.role?.trim() || null, is_primary: true },
          ];
      // Build the payload
      const payload = {
        intl,
        data: {
          customer: customerPayload,
          ...(contactsPayload ? { contacts: contactsPayload } : {}),
          ...projectFormData,
          ...(projectExtraFieldsArray.length > 0 ? { extra_fields: projectExtraFieldsArray } : {}),
        },
      };

      // Dispatch create action
      dispatch({
        type: 'leads/create',
        payload,
        then: (createdLeads: LeadModel[]) => {

          // Refresh leads based on current route
          switch (location.pathname) {
            case ROUTE_PATHS.LEADS_BOARD:
              dispatch({
                type: 'leads/fetch',
                payload: {
                  view: LEAD_VIEWS.KANBAN,
                },
              });
              break;
            case ROUTE_PATHS.LEADS:
              dispatch({
                type: 'leads/fetch',
                payload: {
                  view: LEAD_VIEWS.TABLE,
                },
              });
              break;
          }

          if (addLeadDrawer?.onCreateLead) {
            addLeadDrawer.onCreateLead(createdLeads);
          } else {
            dispatch({
              type: 'leads/toggleLeadDetailsDrawer',
              payload: {
                lead: createdLeads[0],
              },
            });
          }

        },
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <div className={styles.drawerContainer}>
      <Space style={{ width: '100%' }} direction={'vertical'}>
        {/* Related Projects InfoCard */}
        <AddProjectRelatedLeads foundCustomers={foundCustomers} onUseDifferentContact={handleUseDifferentContact} />
        {isCustomerFormDisabled && existingCustomerId && (
          <ProjectContactSelect
            customerId={existingCustomerId}
            value={projectContactId}
            onChange={setProjectContactId}
          />
        )}

        {/* Customer Information Card - Blue */}
        <CustomerForm
          form={customerForm}
          isCustomerFormDisabled={isCustomerFormDisabled}
          isSearchingCustomers={isSearchingCustomers}
          customerRequiredFields={customerRequiredFields}
          onEmailBlur={handleEmailBlur}
          onPhoneBlur={handlePhoneBlur}
          onCustomerExtraFieldValuesChange={setCustomerExtraFieldValues}
          customerType={customerType}
          onCustomerTypeChange={(type) => {
            setCustomerType(type);
            setMatchingCompany(null);
          }}
          onCompanyBlur={handleCompanyBlur}
          matchingCompany={matchingCompany}
          onUseMatchingCompany={handleUseMatchingCompany}
        />

        {/* Project Information Card - Purple */}
        <ProjectForm
          form={projectForm}
          branchId={payloadBranchId}
          fixedBranchService={fixedBranchService}
          fixedBranchServiceLoading={fixedBranchServiceLoading}
          onProjectExtraFieldValuesChange={setProjectExtraFieldValues}
        />

        {/* Submit Button */}
        <Flex justify={'flex-end'}>
          <Button
            data-testid="create-lead-submit-btn"
            type="primary"
            size="large"
            loading={submitLoading}
            onClick={onCreateLead}
            style={{ marginTop: 16 }}
          >
            <F entity={INTL_ENTITY.LEAD} action={INTL_ACTION.CREATE} />
          </Button>
        </Flex>
      </Space>
    </div>
  );
};

export default AddManualLead;
